/**
 * Provider-accounts tab for the tenant detail page (PR-4b).
 *
 * - Lists every provider account on the tenant via
 *   ``GET /admin-api/v1/provider-accounts/?tenant_id=…``.
 * - "+ Add provider" opens a Dialog: pick plugin → render the
 *   per-plugin form → ``POST /provider-accounts/`` → on success
 *   surface the callback URL inline (operator pastes it into the
 *   provider's portal).
 * - Per row: toggle disable/enable, rotate-secrets. For a Daraja account
 *   the rotate dialog offers "Apply to all accounts with this initiator"
 *   (default on when other accounts share it, spec 0010 T9) and a TOTP
 *   field: step-up is required when Senti encrypts the initiator password
 *   or the rotation applies to the siblings (spec 0010 §5.6.1).
 *
 * The callback URL is the only "secret-ish" value the operator
 * actually copies; the encrypted credentials never leave the server
 * after create (rotate-secrets writes new ciphertext but the old
 * plaintext is gone).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  asApiError,
  createProviderAccount,
  getProviderAccount,
  getProviderAccounts,
  patchProviderAccount,
  rotateProviderAccountSecrets,
  type ProviderAccountRow,
  type ProviderAccountStatus,
} from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import {
  PLUGIN_OPTIONS,
  getPluginOption,
  type PluginOption,
} from "./provider-forms";

export function ProvidersTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState<PluginOption | null>(null);
  const [accountName, setAccountName] = useState("");
  const [revealed, setRevealed] = useState<ProviderAccountRow | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ProviderAccountRow | null>(
    null,
  );
  // null = follow the default (on when siblings exist).
  const [applyOverride, setApplyOverride] = useState<boolean | null>(null);
  const [rotateTotp, setRotateTotp] = useState("");

  const isDarajaRotate = rotateTarget?.plugin === "mpesa_daraja";
  const rotateDetail = useQuery({
    queryKey: ["provider-account", rotateTarget?.id] as const,
    queryFn: () => getProviderAccount(rotateTarget!.id),
    enabled: !!rotateTarget && isDarajaRotate,
  });
  const siblings = rotateDetail.data?.initiator_siblings ?? [];
  const applyToSiblings = siblings.length > 0 && (applyOverride ?? true);

  function closeRotate() {
    setRotateTarget(null);
    setApplyOverride(null);
    setRotateTotp("");
  }

  const query = useQuery({
    queryKey: ["provider-accounts", { tenantId }] as const,
    queryFn: () => getProviderAccounts({ tenant_id: tenantId }),
  });

  const createMut = useMutation({
    mutationFn: (vars: { plugin: string; name: string; credentials: Record<string, string> }) =>
      createProviderAccount({
        tenant_id: tenantId,
        plugin: vars.plugin,
        name: vars.name,
        credentials: vars.credentials,
      }),
    onSuccess: async (created) => {
      setAddDialogOpen(false);
      setSelectedPlugin(null);
      setAccountName("");
      setRevealed(created);
      await qc.invalidateQueries({
        queryKey: ["provider-accounts", { tenantId }],
      });
    },
    onError: (err) => {
      const body = asApiError(err);
      toast.error("Couldn't create provider account", {
        description: body?.detail ?? body?.error ?? "Check your network and retry.",
      });
    },
  });

  const patchMut = useMutation({
    mutationFn: (vars: { id: string; status: ProviderAccountStatus }) =>
      patchProviderAccount(vars.id, { status: vars.status }),
    onSuccess: async (_updated, vars) => {
      toast.success(
        vars.status === "disabled"
          ? "Provider account disabled"
          : "Provider account re-enabled",
      );
      await qc.invalidateQueries({
        queryKey: ["provider-accounts", { tenantId }],
      });
    },
    onError: (err) => {
      const body = asApiError(err);
      toast.error("Status change failed", {
        description: body?.detail ?? body?.error ?? "Check your network and retry.",
      });
    },
  });

  const rotateMut = useMutation({
    mutationFn: (vars: {
      id: string;
      credentials: Record<string, string>;
      applyToSiblings: boolean;
      totpCode: string;
    }) =>
      rotateProviderAccountSecrets(vars.id, vars.credentials, {
        apply_to_siblings: vars.applyToSiblings,
        totp_code: vars.totpCode,
      }),
    onSuccess: async (result) => {
      closeRotate();
      const n = result.updated_account_ids?.length ?? 1;
      toast.success(
        n > 1 ? `Provider secrets rotated on ${n} accounts` : "Provider secrets rotated",
      );
      await qc.invalidateQueries({
        queryKey: ["provider-accounts", { tenantId }],
      });
    },
    onError: (err) => {
      const body = asApiError(err);
      toast.error("Couldn't rotate secrets", {
        description: body?.detail ?? body?.error ?? "Check your network and retry.",
      });
    },
  });

  const rows = query.data?.results ?? [];

  function onCreateSubmit(credentials: Record<string, string>) {
    if (!selectedPlugin) return;
    if (!accountName.trim()) {
      toast.error("Name is required");
      return;
    }
    createMut.mutate({
      plugin: selectedPlugin.value,
      name: accountName.trim(),
      credentials,
    });
  }

  function onRotateSubmit(credentials: Record<string, string>) {
    if (!rotateTarget) return;
    rotateMut.mutate({
      id: rotateTarget.id,
      credentials,
      applyToSiblings,
      totpCode: rotateTotp.trim(),
    });
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Provider accounts</CardTitle>
            <CardDescription>
              Upstream payment providers (M-Pesa Daraja, SasaPay, …) this tenant
              dispatches through. Secrets are envelope-encrypted at rest.
            </CardDescription>
          </div>
          <Button onClick={() => setAddDialogOpen(true)}>+ Add provider</Button>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No provider accounts yet. Add one to wire this tenant's outbound
              integration.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plugin</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead aria-label="Actions" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.plugin}</TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{row.name}</div>
                          <div className="truncate font-mono text-xs text-muted-foreground">
                            {row.id}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            row.status === "active"
                              ? "border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300"
                              : "border-slate-300 text-muted-foreground"
                          }
                        >
                          {row.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(row.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRotateTarget(row)}
                            data-testid={`provider-rotate-${row.id}`}
                          >
                            Rotate secrets
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              patchMut.mutate({
                                id: row.id,
                                status:
                                  row.status === "active" ? "disabled" : "active",
                              })
                            }
                            disabled={patchMut.isPending}
                            data-testid={`provider-toggle-${row.id}`}
                          >
                            {row.status === "active" ? "Disable" : "Enable"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add-provider dialog: pick plugin → render form */}
      <Dialog
        open={addDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddDialogOpen(false);
            setSelectedPlugin(null);
            setAccountName("");
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add provider account</DialogTitle>
            <DialogDescription>
              Credentials are validated against the plugin's schema and stored
              envelope-encrypted. The callback URL is shown after creation —
              copy it into the provider's portal.
            </DialogDescription>
          </DialogHeader>

          {!selectedPlugin ? (
            <div className="space-y-2">
              <Label>Pick a plugin</Label>
              {PLUGIN_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedPlugin(opt)}
                  className="w-full rounded-md border p-3 text-left hover:bg-muted/40"
                  data-testid={`provider-plugin-${opt.value}`}
                >
                  <span className="block text-sm font-medium">{opt.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {opt.description}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <span>
                  Plugin: <strong>{selectedPlugin.label}</strong>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPlugin(null)}
                  disabled={createMut.isPending}
                >
                  Change
                </Button>
              </div>
              <div className="space-y-1">
                <Label htmlFor="pa-name">Account name</Label>
                <Input
                  id="pa-name"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. Production daraja"
                  required
                  disabled={createMut.isPending}
                />
              </div>
              {selectedPlugin.form({
                mode: "create",
                submitting: createMut.isPending,
                onSubmit: onCreateSubmit,
                onCancel: () => {
                  setAddDialogOpen(false);
                  setSelectedPlugin(null);
                  setAccountName("");
                },
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reveal dialog after create: surface callback URL.
          DELIBERATELY dismissable (overlay click + Escape) — distinct from
          ``ApiKeyRevealModal`` which is non-dismissable because the
          plaintext is unrecoverable on close. The callback URL is a public
          string (part of the inbound webhook path); the operator can refetch
          it via GET /provider-accounts/{id}/ at any time. Asymmetry is
          intentional. PR #140 review 🔵. */}
      <Dialog
        open={!!revealed}
        onOpenChange={(open) => {
          if (!open) setRevealed(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Provider account created</DialogTitle>
            <DialogDescription>
              Paste this callback URL into the provider's portal so inbound
              webhooks reach Senti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Callback URL</Label>
            {revealed?.callback_url ? (
              <code
                data-testid="provider-callback-url"
                className="block select-all break-all rounded-md border bg-muted/30 p-3 font-mono text-xs"
              >
                {revealed.callback_url}
              </code>
            ) : (
              <p className="text-sm text-muted-foreground">
                The callback URL wasn't rendered (the substrate's public base URL
                is not configured). Build it manually as{" "}
                <code className="font-mono text-xs">
                  &lt;your-public-host&gt;/cb/{revealed?.plugin}/&lt;callback_path_token&gt;/
                </code>
                . Ask an operator with VM access for the token.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate-secrets dialog */}
      <Dialog
        open={!!rotateTarget}
        onOpenChange={(open) => {
          if (!open) closeRotate();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Rotate secrets</DialogTitle>
            <DialogDescription>
              Replace the encrypted secret bag on{" "}
              <strong>{rotateTarget?.name}</strong>. Existing config stays;
              only the secret-classified fields change.
            </DialogDescription>
          </DialogHeader>
          {rotateTarget &&
            (() => {
              const opt = getPluginOption(rotateTarget.plugin);
              if (!opt) {
                return (
                  <p className="text-sm text-destructive">
                    Plugin <code>{rotateTarget.plugin}</code> doesn't have a TS
                    form. Re-create the account against a current plugin.
                  </p>
                );
              }
              return (
                <div className="space-y-4">
                  {isDarajaRotate && (
                    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
                      {siblings.length > 0 && (
                        <div className="flex items-start gap-2">
                          <input
                            id="rotate-apply-siblings"
                            type="checkbox"
                            checked={applyToSiblings}
                            onChange={(e) => setApplyOverride(e.target.checked)}
                            disabled={rotateMut.isPending}
                            className="mt-0.5 h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
                          />
                          <div className="flex-1 space-y-1">
                            <Label htmlFor="rotate-apply-siblings" className="cursor-pointer">
                              Apply to all accounts with this initiator
                            </Label>
                            <p
                              className="text-xs text-muted-foreground"
                              data-testid="rotate-sibling-count"
                            >
                              {siblings.length} other account
                              {siblings.length === 1 ? "" : "s"} share this initiator:{" "}
                              {siblings.map((s) => s.name).join(", ")}. They get the
                              same encrypted credential, so the credential guard
                              treats them as one. Only the initiator credential
                              can be rotated this way.
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label htmlFor="rotate-totp">Authenticator code</Label>
                        <Input
                          id="rotate-totp"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={rotateTotp}
                          onChange={(e) => setRotateTotp(e.target.value)}
                          disabled={rotateMut.isPending}
                        />
                        <p className="text-xs text-muted-foreground">
                          Required when Senti encrypts the initiator password or
                          the change applies to other accounts.
                        </p>
                      </div>
                    </div>
                  )}
                  {opt.form({
                    mode: "rotate",
                    submitting: rotateMut.isPending,
                    onSubmit: onRotateSubmit,
                    onCancel: closeRotate,
                  })}
                </div>
              );
            })()}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ProvidersTab;
